import type { FC } from 'react'
import { useEffect } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, ButtonBase, Typography } from '@mui/material'
import type { UserOption } from '@/index'
import { CustomUserResponseBox, FIXED_TIMESTAMP_SIZE } from '../shared'

interface UserOptionsProps {
  readonly options: readonly UserOption[]
  readonly isActive: boolean
  readonly onOptionSelect?: (index: number) => void
  readonly showTitle?: boolean
  readonly selectedOptionIndex?: number
  readonly customPrompt?: string | null
  readonly selectionColor?: string
}

interface OptionItemProps {
  readonly option: UserOption
  readonly isActive: boolean
  readonly isSelected: boolean
  readonly hasSelection: boolean
  readonly onOptionSelect?: ((index: number) => void) | undefined
  readonly customPrompt?: string | null | undefined
  readonly selectionColor: string
}

export const UserOptionsRenderer: FC<UserOptionsProps> = ({
  options,
  isActive,
  onOptionSelect,
  showTitle = false,
  selectedOptionIndex,
  customPrompt,
  selectionColor = 'primary.main',
}) => {
  const hasSelection = selectedOptionIndex !== undefined

  useEffect(() => {
    if (!isActive || !onOptionSelect || hasSelection) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key

      if (key >= '1' && key <= '9') {
        const pressedIndex = Number.parseInt(key, 10)

        const option = options.find((opt) => opt.index === pressedIndex)
        if (option) {
          event.preventDefault()
          onOptionSelect(pressedIndex)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isActive, options, onOptionSelect, hasSelection])

  return (
    <Box>
      {showTitle && (
        <Typography variant="subtitle1" sx={titleTextSx}>
          Your options
        </Typography>
      )}

      <Box component="ol" sx={optionsListSx} aria-live="polite">
        {options.map((option) => (
          <OptionItem
            key={option.index}
            option={option}
            isActive={isActive && !hasSelection}
            isSelected={selectedOptionIndex === option.index}
            hasSelection={hasSelection}
            onOptionSelect={onOptionSelect}
            customPrompt={selectedOptionIndex === option.index ? customPrompt : null}
            selectionColor={selectionColor}
          />
        ))}
      </Box>

      {isActive && (
        <Box sx={hintContainerSx}>
          <Typography variant="caption" sx={hintTextSx}>
            Press 1, 2, 3 or click to select
          </Typography>
        </Box>
      )}
    </Box>
  )
}

const OptionItem: FC<OptionItemProps> = ({
  option,
  isActive,
  isSelected,
  hasSelection,
  onOptionSelect,
  customPrompt,
  selectionColor,
}) => {
  const handleClick = () => {
    if (isActive && onOptionSelect) {
      onOptionSelect(option.index)
    }
  }

  return (
    <Box component="li" sx={optionItemContainerSx}>
      <ButtonBase onClick={handleClick} disabled={!isActive} sx={optionButtonSx(isActive)}>
        <Typography
          component="span"
          sx={indexTextSx(isActive, isSelected, hasSelection, selectionColor)}
        >
          {option.index}
        </Typography>
        <Typography sx={optionTextSx(isActive, isSelected, hasSelection, selectionColor)}>
          {option.option}
        </Typography>
      </ButtonBase>
      {isSelected && customPrompt && (
        <Box sx={customPromptContainerSx}>
          <CustomUserResponseBox content={customPrompt} color={selectionColor} />
        </Box>
      )}
    </Box>
  )
}

const titleTextSx: SxProps<Theme> = {
  mb: 2,
  fontWeight: 600,
  fontSize: 'inherit',
}

const optionsListSx: SxProps<Theme> = {
  pl: 0,
  listStyle: 'none',
}

const optionButtonSx = (isActive: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  textAlign: 'left',
  borderRadius: 1,
  p: 1,
  transition: 'all 0.2s ease-in-out',
  cursor: isActive ? 'pointer' : 'default',
  fontSize: 'inherit',
  '&:hover': {
    backgroundColor: isActive ? 'action.hover' : 'transparent',
  },
  '&:active': {
    backgroundColor: isActive ? 'action.selected' : 'transparent',
  },
})

const indexTextSx = (
  isActive: boolean,
  isSelected: boolean,
  hasSelection: boolean,
  selectionColor: string,
): SxProps<Theme> => {
  let color: string
  if (isSelected) {
    color = selectionColor
  } else if (hasSelection || !isActive) {
    color = 'text.disabled'
  } else {
    color = selectionColor
  }

  return {
    minWidth: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    color,
    fontWeight: 700,
    mr: 1,
    flexShrink: 0,
    fontSize: 'inherit',
  }
}

const optionTextSx = (
  isActive: boolean,
  isSelected: boolean,
  hasSelection: boolean,
  selectionColor: string,
): SxProps<Theme> => {
  let color: string
  if (isSelected) {
    color = selectionColor
  } else if (hasSelection || !isActive) {
    color = 'text.disabled'
  } else {
    color = 'text.primary'
  }

  return {
    lineHeight: 1.4,
    flex: 1,
    color,
    fontWeight: isSelected ? 600 : 400,
    fontSize: 'inherit',
  }
}

const hintContainerSx: SxProps<Theme> = {
  mt: 3,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

const hintTextSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontStyle: 'italic',
  opacity: 0.6,
  fontSize: FIXED_TIMESTAMP_SIZE,
}

const optionItemContainerSx: SxProps<Theme> = {
  mb: 1.5,
}

const customPromptContainerSx: SxProps<Theme> = {
  pl: 4,
  pt: 0.5,
  pb: 0.5,
}
